import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../config';
import LoadingSpinner from './LoadingSpinner';
import OfflineWalletComponent from './OfflineWalletComponent';
import CualiaAdvice from './CualiaAdvice';

const LARGE_TX_THRESHOLD = 100; // umbral en Cc/USD para activar CUALia

const WalletComponent = () => {
  const [walletData, setWalletData] = useState(null);
  const [ledgerData, setLedgerData] = useState(null);
  const [currencies, setCurrencies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('ledger');
  const [showRecharge, setShowRecharge] = useState(false);
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [rechargeCurrency, setRechargeCurrency] = useState('CUALCOIN');
  const [rechargeNote, setRechargeNote] = useState('');
  const [recharging, setRecharging] = useState(false);
  const [showCualiaForRecharge, setShowCualiaForRecharge] = useState(false);
  const [pendingRechargeAmount, setPendingRechargeAmount] = useState(null);

  useEffect(() => {
    fetchWalletData();
    fetchLedgerData();
    fetchCurrencies();
  }, []);

  const fetchWalletData = async () => {
    try {
      const response = await axios.get(`${API}/wallet/balance`);
      setWalletData(response.data);
    } catch (error) {
      console.error('Failed to fetch wallet data:', error);
    }
  };

  const fetchLedgerData = async () => {
    try {
      const response = await axios.get(`${API}/wallet/balance/ledger`);
      setLedgerData(response.data);
    } catch (error) {
      console.error('Failed to fetch ledger data:', error);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response = await axios.get(`${API}/wallet/currencies`);
      setCurrencies(response.data);
    } catch (error) {
      console.error('Failed to fetch currencies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRecharge = async () => {
    const amt = parseFloat(rechargeAmount);
    if (!amt || amt <= 0) { alert('Ingresa un monto valido'); return; }
    // Si supera el umbral, pedimos a CUALia que muestre su conciencia antes
    if (amt >= LARGE_TX_THRESHOLD && !showCualiaForRecharge && pendingRechargeAmount !== amt) {
      setPendingRechargeAmount(amt);
      setShowCualiaForRecharge(true);
      return;
    }
    setRecharging(true);
    try {
      const res = await axios.post(`${API}/wallet/recharge`, {
        amount: amt,
        currency: rechargeCurrency,
        note: rechargeNote
      });
      alert(res.data.message);
      setShowRecharge(false);
      setRechargeAmount('');
      setRechargeNote('');
      setPendingRechargeAmount(null);
      fetchWalletData();
      fetchLedgerData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al recargar');
    } finally {
      setRecharging(false);
    }
  };

  const handleCualiaAck = () => {
    setShowCualiaForRecharge(false);
    // pendingRechargeAmount permanece para que handleRecharge proceda sin re-disparar el overlay
    handleRecharge();
  };

  const handleCualiaCancel = () => {
    setShowCualiaForRecharge(false);
    setPendingRechargeAmount(null);
  };

  if (loading) return <LoadingSpinner />;

  const getCurrencyInfo = (code) => {
    if (!currencies) return { symbol: code, name: code };
    
    // Check native currency
    if (currencies.native_currency?.code === code) {
      return currencies.native_currency;
    }
    // Check credit unit
    if (currencies.credit_unit?.code === code) {
      return currencies.credit_unit;
    }
    // Check fiat
    const fiat = currencies.fiat_currencies?.find(c => c.code === code);
    if (fiat) return fiat;
    // Check crypto
    const crypto = currencies.cryptocurrencies?.find(c => c.code === code);
    if (crypto) return crypto;
    
    return { symbol: code, name: code };
  };

  const getCurrencyClass = (code) => {
    if (code === 'CUALCOIN') return 'border-l-4 border-purple-500 bg-gradient-to-r from-purple-50 to-blue-50';
    if (code === 'PUNTOS_VERDES') return 'border-l-4 border-green-500 bg-gradient-to-r from-green-50 to-emerald-50';
    if (code.startsWith('CUAL-') && code.includes('BTC')) return 'border-l-4 border-orange-500 bg-gradient-to-r from-orange-50 to-amber-50';
    if (code.startsWith('CUAL-') && (code.includes('ETH') || code.includes('USDT'))) return 'border-l-4 border-blue-500 bg-gradient-to-r from-blue-50 to-cyan-50';
    return 'border-l-4 border-gray-300';
  };

  const getCurrencyBadge = (code) => {
    if (code === 'CUALCOIN') return { text: 'CUALcoin', color: 'text-purple-600 bg-purple-100' };
    if (code === 'PUNTOS_VERDES') return { text: 'Crédito Estable', color: 'text-green-600 bg-green-100' };
    if (code.startsWith('CUAL-') && (code.includes('BTC') || code.includes('ETH') || code.includes('USDT'))) {
      return { text: 'Crypto', color: 'text-orange-600 bg-orange-100' };
    }
    if (code.startsWith('CUAL-')) return { text: 'Fiat', color: 'text-blue-600 bg-blue-100' };
    return null;
  };

  // Obtener balances desde el Ledger (fuente de verdad)
  const ledgerBalances = ledgerData?.balances || {};
  
  // Ordenar y filtrar balances con saldo > 0 o monedas principales
  const sortedLedgerBalances = Object.entries(ledgerBalances)
    .filter(([code, balances]) => {
      return code === 'CUALCOIN' || code === 'PUNTOS_VERDES' || 
             balances.total > 0 || balances.retained > 0 || balances.pending > 0;
    })
    .sort((a, b) => {
      const order = ['CUALCOIN', 'PUNTOS_VERDES'];
      const aIndex = order.indexOf(a[0]);
      const bIndex = order.indexOf(b[0]);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a[0].localeCompare(b[0]);
    });

  return (
    <div className="space-y-6">
      {showCualiaForRecharge && (
        <CualiaAdvice
          contextType="large_transaction"
          metadata={{ amount: pendingRechargeAmount, currency: rechargeCurrency, action: 'recharge' }}
          onAcknowledge={handleCualiaAck}
          onCancel={handleCualiaCancel}
        />
      )}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">Mi Wallet CUALAPP</h2>
        <p className="text-gray-600">Sistema CUAL-Multimoneda</p>
        {walletData?.architecture_version === '3_capas_alfa' && (
          <span className="inline-block mt-2 text-xs bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 px-3 py-1 rounded-full">
            Arquitectura Financiera 3 Capas
          </span>
        )}
      </div>

      {/* Selector de Vista + Recargar */}
      <div className="flex flex-wrap justify-center items-center gap-2">
        <button
          onClick={() => setViewMode('ledger')}
          data-testid="view-ledger-btn"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            viewMode === 'ledger' 
              ? 'bg-purple-600 text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Vista Ledger (Fuente de Verdad)
        </button>
        <button
          onClick={() => setViewMode('legacy')}
          data-testid="view-legacy-btn"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            viewMode === 'legacy' 
              ? 'bg-gray-600 text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Vista Legacy
        </button>
        <button
          onClick={() => setViewMode('offline')}
          data-testid="view-offline-btn"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
            viewMode === 'offline' 
              ? 'bg-amber-600 text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12 20.5h.01"/>
          </svg>
          Offline
        </button>
        <button
          onClick={() => { setShowRecharge(!showRecharge); setPendingRechargeAmount(null); }}
          data-testid="recharge-btn"
          className="px-4 py-2 rounded-lg text-sm font-medium transition-all bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600 shadow-sm"
        >
          {showRecharge ? 'Cerrar' : 'Recargar Recursos'}
        </button>
      </div>

      {/* Recharge Form */}
      {showRecharge && (
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-200 p-5" data-testid="recharge-form">
          <h3 className="font-semibold text-gray-800 mb-3">Recargar Recursos</h3>
          <p className="text-sm text-gray-600 mb-4">Ingresa la cantidad que deseas gestionar dentro de CUALAPP. Puedes recargar en cualquier momento.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Monto</label>
              <input
                type="number"
                value={rechargeAmount}
                onChange={(e) => setRechargeAmount(e.target.value)}
                placeholder="0.00"
                data-testid="recharge-amount"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Moneda</label>
              <select
                value={rechargeCurrency}
                onChange={(e) => setRechargeCurrency(e.target.value)}
                data-testid="recharge-currency"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              >
                <option value="CUALCOIN">CUALcoin</option>
                <option value="CUAL-USD">CUAL-USD</option>
                <option value="CUAL-EUR">CUAL-EUR</option>
                <option value="CUAL-VES">CUAL-VES</option>
                <option value="CUAL-BTC">CUAL-BTC</option>
                <option value="CUAL-USDT">CUAL-USDT</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nota (opcional)</label>
              <input
                type="text"
                value={rechargeNote}
                onChange={(e) => setRechargeNote(e.target.value)}
                placeholder="Para que necesitas estos recursos?"
                data-testid="recharge-note"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>
          <div className="flex items-center justify-between mt-4">
            <p className="text-xs text-gray-500">Flujo simulado. Pasarela de pago pendiente.</p>
            <button
              onClick={handleRecharge}
              disabled={recharging}
              data-testid="confirm-recharge-btn"
              className="px-6 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg hover:from-emerald-600 hover:to-teal-600 font-medium disabled:opacity-50"
            >
              {recharging ? 'Procesando...' : 'Confirmar Recarga'}
            </button>
          </div>
        </div>
      )}

      {viewMode === 'offline' ? (
        <OfflineWalletComponent />
      ) : viewMode === 'ledger' ? (
        <>
          {/* Monedas principales desde Ledger */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sortedLedgerBalances
              .filter(([code]) => code === 'CUALCOIN' || code === 'PUNTOS_VERDES')
              .map(([currency, balances]) => {
                const info = getCurrencyInfo(currency);
                const badge = getCurrencyBadge(currency);
                return (
                  <div 
                    key={currency} 
                    data-testid={`ledger-balance-${currency}`}
                    className={`bg-white rounded-xl shadow-lg p-6 ${getCurrencyClass(currency)}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {currency === 'CUALCOIN' && (
                          <img src="/cualcoin-icon.png" alt="CUALcoin" className="w-12 h-12 flex-shrink-0" data-testid="cualcoin-icon-wallet" draggable="false" />
                        )}
                        <div>
                          <p className="text-lg font-bold text-gray-900">{info.name}</p>
                          <p className="text-sm text-gray-500">{currency}</p>
                          {badge && (
                            <span className={`inline-block text-xs font-medium mt-2 px-2 py-1 rounded ${badge.color}`}>
                              {badge.text}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-2xl font-bold ${
                          currency === 'CUALCOIN' ? 'text-purple-600' : 'text-green-600'
                        }`}>
                          {info.symbol} {balances.available.toLocaleString()}
                        </p>
                        {/* Mostrar saldos adicionales si existen */}
                        {balances.retained > 0 && (
                          <p className="text-xs text-orange-600 mt-1">
                            Retenido: {balances.retained.toLocaleString()}
                          </p>
                        )}
                        {balances.pending > 0 && (
                          <p className="text-xs text-yellow-600">
                            Pendiente: {balances.pending.toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>

          {/* Otras monedas desde Ledger */}
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-3">CUAL-Multimonedas</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {sortedLedgerBalances
                .filter(([code]) => code !== 'CUALCOIN' && code !== 'PUNTOS_VERDES')
                .map(([currency, balances]) => {
                  const info = getCurrencyInfo(currency);
                  const badge = getCurrencyBadge(currency);
                  return (
                    <div 
                      key={currency} 
                      data-testid={`ledger-balance-${currency}`}
                      className={`bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-all duration-200 ${getCurrencyClass(currency)}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-800">{info.name}</p>
                          <p className="text-xs text-gray-500">{currency}</p>
                          {badge && (
                            <span className={`inline-block text-xs font-medium mt-1 px-1.5 py-0.5 rounded ${badge.color}`}>
                              {badge.text}
                            </span>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900">
                            {info.symbol} {balances.available.toLocaleString()}
                          </p>
                          {balances.retained > 0 && (
                            <p className="text-xs text-orange-600">
                              🔒 {balances.retained.toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Leyenda de tipos de saldo */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Tipos de Saldo</h4>
            <div className="flex flex-wrap gap-4 text-xs">
              <div className="flex items-center space-x-1">
                <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                <span>Disponible: Puedes usar libremente</span>
              </div>
              <div className="flex items-center space-x-1">
                <span className="w-3 h-3 bg-orange-500 rounded-full"></span>
                <span>Retenido: En escrow para comercio P2P</span>
              </div>
              <div className="flex items-center space-x-1">
                <span className="w-3 h-3 bg-yellow-500 rounded-full"></span>
                <span>Pendiente: En proceso de confirmación</span>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Vista Legacy (antiguo sistema) */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <p className="text-xs text-yellow-700">
              Esta es la vista del sistema antiguo. El Ledger es la fuente de verdad.
            </p>
          </div>
          
          {/* Monedas principales - Legacy */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {walletData && Object.entries(walletData.balances)
              .filter(([code]) => code === 'CUALCOIN' || code === 'PUNTOS_VERDES' || code === 'CUAL')
              .map(([currency, balance]) => {
                const info = getCurrencyInfo(currency);
                const badge = getCurrencyBadge(currency);
                return (
                  <div 
                    key={currency} 
                    className={`bg-white rounded-xl shadow-lg p-6 ${getCurrencyClass(currency)}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-lg font-bold text-gray-900">{info.name}</p>
                        <p className="text-sm text-gray-500">{currency}</p>
                        {badge && (
                          <span className={`inline-block text-xs font-medium mt-2 px-2 py-1 rounded ${badge.color}`}>
                            {badge.text}
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        <p className={`text-2xl font-bold ${
                          currency === 'CUALCOIN' || currency === 'CUAL' ? 'text-purple-600' : 'text-green-600'
                        }`}>
                          {info.symbol} {balance.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>

          {/* Otras monedas - Legacy */}
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-3">CUAL-Multimonedas</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {walletData && Object.entries(walletData.balances)
                .filter(([code]) => code !== 'CUALCOIN' && code !== 'PUNTOS_VERDES' && code !== 'CUAL')
                .map(([currency, balance]) => {
                  const info = getCurrencyInfo(currency);
                  const badge = getCurrencyBadge(currency);
                  return (
                    <div 
                      key={currency} 
                      className={`bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-all duration-200 ${getCurrencyClass(currency)}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-800">{info.name}</p>
                          <p className="text-xs text-gray-500">{currency}</p>
                          {badge && (
                            <span className={`inline-block text-xs font-medium mt-1 px-1.5 py-0.5 rounded ${badge.color}`}>
                              {badge.text}
                            </span>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900">
                            {info.symbol} {balance.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </>
      )}
      
      {/* Info boxes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {currencies?.native_currency && (
          <div className="bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg p-4 border border-purple-200">
            <h3 className="text-sm font-semibold text-purple-700">CUALcoin</h3>
            <p className="text-xs text-purple-600 mt-1">{currencies.native_currency.description}</p>
            <p className="text-xs text-purple-500 mt-1">
              Unidad fraccionaria: {currencies.native_currency.fractional_unit}
            </p>
          </div>
        )}
        {currencies?.credit_unit && (
          <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg p-4 border border-green-200">
            <h3 className="text-sm font-semibold text-green-700">CUALverde <span className="text-[10px] text-gray-400 font-normal">(CV)</span></h3>
            <p className="text-xs text-green-600 mt-1">{currencies.credit_unit.description}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Transactions Component  


export default WalletComponent;
